// GlobalTranz specific constants

export const GLOBALTRANZ_STATUS = {
  RateConSent: 'Rate Con Sent',
  InTransit: 'In Transit',
  Delivered: 'Delivered',
  Canceled: 'Canceled',
  Completed: 'Completed',
  Open: 'Open',
  AtShipper: 'At Shipper',
  AtConsignee: 'At Consignee',
  Problem: 'Problem',
  CustomerHold: 'Customer Hold',
  Dispatched: 'Dispatched',
} as const;

export type GlobalTranzStatus =
  (typeof GLOBALTRANZ_STATUS)[keyof typeof GLOBALTRANZ_STATUS];

const GTZ_OPERATIONAL_STATUS_OPTIONS: GlobalTranzStatus[] = [
  GLOBALTRANZ_STATUS.RateConSent,
  GLOBALTRANZ_STATUS.Dispatched,
  GLOBALTRANZ_STATUS.AtShipper,
  GLOBALTRANZ_STATUS.InTransit,
  GLOBALTRANZ_STATUS.AtConsignee,
  GLOBALTRANZ_STATUS.Delivered,
  GLOBALTRANZ_STATUS.Completed,
  GLOBALTRANZ_STATUS.Problem,
];

// Mirrors GTZ's client-side status dropdown filter from the sales-order TL bundle.
export function getGlobalTranzStatusTransitionOptions(
  currentStatus?: string
): GlobalTranzStatus[] {
  switch (currentStatus) {
    case GLOBALTRANZ_STATUS.Open:
      return [GLOBALTRANZ_STATUS.CustomerHold, GLOBALTRANZ_STATUS.Problem];
    case GLOBALTRANZ_STATUS.CustomerHold:
      return [GLOBALTRANZ_STATUS.Open];
    case GLOBALTRANZ_STATUS.Dispatched:
      return GTZ_OPERATIONAL_STATUS_OPTIONS.filter(
        (status) => status !== GLOBALTRANZ_STATUS.Dispatched
      );
    case GLOBALTRANZ_STATUS.RateConSent:
      return GTZ_OPERATIONAL_STATUS_OPTIONS.filter(
        (status) => status !== GLOBALTRANZ_STATUS.RateConSent
      );
    case GLOBALTRANZ_STATUS.AtShipper:
      return GTZ_OPERATIONAL_STATUS_OPTIONS.filter(
        (status) => status !== GLOBALTRANZ_STATUS.AtShipper
      );
    case GLOBALTRANZ_STATUS.InTransit:
      return GTZ_OPERATIONAL_STATUS_OPTIONS.filter(
        (status) => status !== GLOBALTRANZ_STATUS.InTransit
      );
    case GLOBALTRANZ_STATUS.AtConsignee:
      return GTZ_OPERATIONAL_STATUS_OPTIONS.filter(
        (status) => status !== GLOBALTRANZ_STATUS.AtConsignee
      );
    case GLOBALTRANZ_STATUS.Delivered:
      return GTZ_OPERATIONAL_STATUS_OPTIONS.filter(
        (status) => status !== GLOBALTRANZ_STATUS.Delivered
      );
    case GLOBALTRANZ_STATUS.Problem:
      return [
        GLOBALTRANZ_STATUS.Dispatched,
        GLOBALTRANZ_STATUS.AtShipper,
        GLOBALTRANZ_STATUS.InTransit,
        GLOBALTRANZ_STATUS.AtConsignee,
        GLOBALTRANZ_STATUS.Completed,
        GLOBALTRANZ_STATUS.Delivered,
        GLOBALTRANZ_STATUS.Problem,
      ];
    default:
      return [];
  }
}

export const currencyList: string[] = ['USD'];

// GlobalTranz service types
export const globalTranzServiceTypes: string[] = [
  'LTL',
  'LTL Manual',
  'LTL Cost Only',
  'TL',
  'TL E-tender',
];

// GlobalTranz service options (for TL)
export const globalTranzServiceOptions = [
  { value: 1, label: 'Full' },
  { value: 2, label: 'Partial' },
  { value: 4, label: 'Volume' },
];

// GlobalTranz shipment types (for TL and TL E-tender)
export const globalTranzShipmentTypes: string[] = [
  'Spot Quote',
  'Contract Ongoing',
];

// GlobalTranz equipment types - from GlobalTranz API response
export const globalTranzEquipmentList: string[] = [
  'Van',
  'Flatbed',
  'Temp Controlled (Frozen)',
  'Temp Controlled (Refrigerated)',
  'Power Only',
  'RGN',
  'Conestoga',
  'Curtainside',
  'Straight Truck',
  'Double Drop',
  'Cargo',
  'Container',
  'Sprinter Van',
  'TOFC',
  'COFC',
  'Bulk',
  'Cargo Van',
  'Partial TL',
  'Drayage',
  'Stepdeck',
  'Other',
  'Hot Shot',
  'Van or Reefer',
  'Flat or Stepdeck',
  'Van or Intermodal',
  'Intermodal',
  'Reefer Intermodal',
];

// GlobalTranz equipment size/length options
export const globalTranzEquipmentSizeList: string[] = [
  'Other',
  '8 ft',
  '10 ft',
  '12 ft',
  '14 ft',
  '16 ft',
  '20 ft',
  '24 ft',
  '26 ft',
  '28 ft',
  '40 ft',
  '42 ft',
  '45 ft',
  '48 ft',
  '50 ft',
  '53 ft',
];

// Weight units
export const weightUnitsList: string[] = ['t', 'oz', 'ton', 'g', 'lb', 'kg'];

// Accessorials / Special Services: map API display key → site display value (main and sub fields)
// If key not in map, display key as-is
const ACCESSORIAL_DISPLAY_MAP: Record<string, string> = {
  'Limited Pickup': 'Limited P/U',
  'Limited Delivery': 'Limited Div.',
  '# of Pallets': 'Pieces Count',
  LengthInHours: 'Length (Hours)',
  'Origin Liftgate': 'Origin Lift Gate',
  'Destination Liftgate': 'Destination Lift Gate',
};

export function getAccessorialDisplayValue(key: string): string {
  return ACCESSORIAL_DISPLAY_MAP[key] ?? key;
}

// Tarps accessorial sub-field: Tarp Size dropdown options (matches site)
export const TARP_SIZE_OPTIONS = [
  'Not Specified',
  'Smoke',
  '4 Foot',
  '6 Foot',
  '8 Foot',
  'Full',
] as const;
